import React, { useState, useEffect, useRef } from "react";
import {
  Box, Button, Paper, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Chip,
  OutlinedInput, Checkbox, ListItemText, Typography,
  Stack, Tooltip, Snackbar, Grid, TextField, IconButton
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { Description as DocumentIcon } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import { getNewsletterNormas } from "../../componentes/http/actions/NewsletterAction";
import { getOrganismoEmisorHabilitado } from "../../componentes/http/actions/OrganismoEmisorAction";
import NewsletterPreview from "./NewsletterPreview";

const STORAGE_KEY = "newsletter_estado";

const makeParKey = (orgId, tipoId) => `${orgId}_${tipoId}`;
const parseParKey = (key) => {
  const [orgId, tipoId] = key.split("_").map(Number);
  return { orgId, tipoId };
};

const serializarEstado = (estado) =>
  JSON.stringify({
    ...estado,
    fechaDesde: estado.fechaDesde?.toISOString() ?? null,
    fechaHasta: estado.fechaHasta?.toISOString() ?? null,
    eliminadas: [...estado.eliminadas],
  });

const deserializarEstado = (raw) => {
  const parsed = JSON.parse(raw);
  return {
    ...parsed,
    fechaDesde: parsed.fechaDesde ? new Date(parsed.fechaDesde) : null,
    fechaHasta: parsed.fechaHasta ? new Date(parsed.fechaHasta) : null,
    eliminadas: new Set(parsed.eliminadas ?? []),
  };
};

const DEFAULT_FOOTER =
  "El objetivo del sistema es mantener un repositorio de la normativa emitida por los principales reguladores del país en el ámbito financiero y realizar el seguimiento del cumplimiento de los compromisos asociados a dichas normativas. " +
  "Esto se visualiza a través de un panel de control con indicadores de cumplimiento, además de envíos de correos o notificaciones a los responsables de ejecutar las tareas, y la posterior generación de informes de gestión." +
  "\n\nEl servicio de envío diario de modificaciones normativas forma parte de Alerta Normativa Global.\n\nEsperando que esta información le haya sido útil, se despide atentamente,";

const buildDescripcionAuto = (fechaDesde, fechaHasta) => {
  const desde = format(fechaDesde, "d", { locale: es });
  const hasta = format(fechaHasta, "d 'de' MMMM 'de' yyyy", { locale: es });
  return `Austranet les da la bienvenida al Newsletter de nuestra empresa, correspondiente al mes de ${format(fechaDesde, "MMMM", { locale: es })} de ${format(fechaDesde, "yyyy")}. Se incluye en este boletín informativo legal, normativa relevante dictada desde el día ${desde} al ${hasta}, emitida por los organismos reguladores seleccionados.`;
};

const Newsletter = () => {
  const mesAnterior = subMonths(new Date(), 1);

  const estadoGuardado = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? deserializarEstado(raw) : null;
    } catch {
      return null;
    }
  })();

  const [fechaDesde, setFechaDesde] = useState(estadoGuardado?.fechaDesde ?? startOfMonth(mesAnterior));
  const [fechaHasta, setFechaHasta] = useState(estadoGuardado?.fechaHasta ?? endOfMonth(mesAnterior));
  const [selectedOrganismos, setSelectedOrganismos] = useState(estadoGuardado?.selectedOrganismos ?? []);
  const [selectedTipos, setSelectedTipos] = useState(estadoGuardado?.selectedTipos ?? []);
  const [expandedOrganismos, setExpandedOrganismos] = useState(estadoGuardado?.expandedOrganismos ?? {});
  const [newsletterData, setNewsletterData] = useState(estadoGuardado?.newsletterData ?? null);
  const [numeroInforme, setNumeroInforme] = useState(estadoGuardado?.numeroInforme ?? "");
  const [descripcion, setDescripcion] = useState(estadoGuardado?.descripcion ?? "");
  const [destacado, setDestacado] = useState(estadoGuardado?.destacado ?? "");
  const [footer, setFooter] = useState(estadoGuardado?.footer ?? DEFAULT_FOOTER);
  const [eliminadas, setEliminadas] = useState(estadoGuardado?.eliminadas ?? new Set());
  const [edicionesCeldas, setEdicionesCeldas] = useState(estadoGuardado?.edicionesCeldas ?? {});

  const [organismos, setOrganismos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const printRef = useRef(null);

  useEffect(() => {
    if (!newsletterData) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        serializarEstado({
          fechaDesde,
          fechaHasta,
          selectedOrganismos,
          selectedTipos,
          expandedOrganismos,
          newsletterData,
          numeroInforme,
          descripcion,
          destacado,
          footer,
          eliminadas,
          edicionesCeldas,
        })
      );
    } catch (e) {
      console.warn("No se pudo guardar el estado en localStorage:", e);
    }
  }, [
    fechaDesde, fechaHasta, selectedOrganismos, selectedTipos,
    expandedOrganismos, newsletterData, numeroInforme,
    descripcion, destacado, footer, eliminadas, edicionesCeldas,
  ]);

  useEffect(() => {
    if (estadoGuardado?.descripcion) return;
    if (fechaDesde && fechaHasta) {
      setDescripcion(buildDescripcionAuto(fechaDesde, fechaHasta));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getOrganismoEmisorHabilitado(localStorage.getItem("token"))
      .then((response) => {
        const habilitados = Array.isArray(response) ? response.filter((o) => o.habilitado) : [];
        setOrganismos(habilitados);
      })
      .catch((error) => console.error("Error cargando organismos:", error));
  }, []);

  const handleEliminar = (uid) =>
    setEliminadas((prev) => new Set([...prev, uid]));

  const handleEditarCelda = (uid, campo, valor) =>
    setEdicionesCeldas((prev) => ({
      ...prev,
      [uid]: { ...prev[uid], [campo]: valor },
    }));

  const handleLimpiar = () => {
    localStorage.removeItem(STORAGE_KEY);
    const mesAnt = subMonths(new Date(), 1);
    const desde = startOfMonth(mesAnt);
    const hasta = endOfMonth(mesAnt);
    setFechaDesde(desde);
    setFechaHasta(hasta);
    setSelectedOrganismos([]);
    setSelectedTipos([]);
    setExpandedOrganismos({});
    setNewsletterData(null);
    setNumeroInforme("");
    setDestacado("");
    setFooter(DEFAULT_FOOTER);
    setDescripcion(buildDescripcionAuto(desde, hasta));
    setEliminadas(new Set());
    setEdicionesCeldas({});
  };

  const handleOrganismoChange = (orgId, checked) => {
    setSelectedOrganismos((prev) => checked ? [...prev, orgId] : prev.filter((id) => id !== orgId));
    if (!checked) {
      setSelectedTipos((prev) => prev.filter((key) => parseParKey(key).orgId !== orgId));
    }
    setExpandedOrganismos((prev) => ({ ...prev, [orgId]: checked }));
  };

  const handleTipoChange = (orgId, tipoId, checked) => {
    const key = makeParKey(orgId, tipoId);
    setSelectedTipos((prev) => checked ? [...prev, key] : prev.filter((k) => k !== key));
  };

  const handleToggleExpand = (orgId) => {
    setExpandedOrganismos((prev) => ({ ...prev, [orgId]: !prev[orgId] }));
  };

  const getTiposSeleccionadosDeOrg = (orgId) =>
    selectedTipos.filter((key) => parseParKey(key).orgId === orgId).map((key) => parseParKey(key).tipoId);

  const isTipoSelected = (orgId, tipoId) => selectedTipos.includes(makeParKey(orgId, tipoId));

  const buildFiltrosOrganismoTipo = () => {
    if (selectedOrganismos.length === 0) return [];
    return selectedOrganismos.flatMap((orgId) => {
      const tiposElegidos = getTiposSeleccionadosDeOrg(orgId);
      return tiposElegidos.length > 0
        ? tiposElegidos.map((tipoId) => ({ organismoEmisorId: orgId, tipoDocumentoId: tipoId }))
        : [{ organismoEmisorId: orgId, tipoDocumentoId: 0 }];
    });
  };

  const handleBuscar = () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    const desde = format(fechaDesde, "yyyy-MM-dd");
    const hasta = format(fechaHasta, "yyyy-MM-dd");
    const filtros = buildFiltrosOrganismoTipo();

    getNewsletterNormas(token, desde, hasta, filtros)
      .then((data) => {
        setNewsletterData(data);
        setEliminadas(new Set());
        setEdicionesCeldas({});
        setSnackbar({
          open: true,
          message: `Se encontraron ${data.totalNormas} normas en ${data.grupos.length} organismos.`,
          severity: "success",
        });
      })
      .catch((error) => {
        console.error("Error al obtener normas:", error);
        setSnackbar({ open: true, message: "Error al obtener las normas. Verifique la conexión.", severity: "error" });
      })
      .finally(() => setLoading(false));
  };

  const handleExportPdf = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Newsletter_N${numeroInforme}_${fechaDesde ? format(fechaDesde, "yyyy-MM") : ""}`,
    onBeforePrint: () => {
      printRef.current?.querySelectorAll(".nl-edit-label")?.forEach((el) => (el.style.display = "none"));
      return Promise.resolve();
    },
    onAfterPrint: () => {
      printRef.current?.querySelectorAll(".nl-edit-label")?.forEach((el) => (el.style.display = ""));
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Grid container direction="column" spacing={2} sx={{ paddingTop: { xs: "20px", sm: "10px" } }}>
        <Grid item>
          <Typography variant="h4">Generador de Newsletter</Typography>
        </Grid>

        <Grid item>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>Filtros</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
              <DatePicker
                label="Fecha desde"
                value={fechaDesde}
                onChange={setFechaDesde}
                format="dd/MM/yyyy"
                slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
              />
              <DatePicker
                label="Fecha hasta"
                value={fechaHasta}
                onChange={setFechaHasta}
                format="dd/MM/yyyy"
                slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
              />
              <TextField
                label="N° Informe"
                value={numeroInforme}
                onChange={(e) => setNumeroInforme(e.target.value)}
                size="small"
                sx={{ minWidth: 100, maxWidth: 130 }}
              />

              <FormControl size="small" sx={{ minWidth: 500 }}>
                <InputLabel shrink>Organismos y tipos</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={selectedOrganismos}
                  onChange={() => {}}
                  input={<OutlinedInput notched label="Organismos y tipos" />}
                  renderValue={() => {
                    if (selectedOrganismos.length === 0)
                      return <Typography variant="body2" color="text.secondary">Todos los organismos</Typography>;
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedOrganismos.map((orgId) => {
                          const org = organismos.find((o) => o.id === orgId);
                          const tiposCount = getTiposSeleccionadosDeOrg(orgId).length;
                          return (
                            <Chip
                              key={orgId}
                              label={tiposCount > 0 ? `${org?.sigla} (${tiposCount})` : org?.sigla}
                              size="small"
                              color={tiposCount > 0 ? "secondary" : "default"}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    disableAutoFocusItem: true,
                    PaperProps: { sx: { maxHeight: 450, width: 360 } },
                  }}
                >
                  {organismos.map((org) => {
                    const isSelected = selectedOrganismos.includes(org.id);
                    const isExpanded = expandedOrganismos[org.id] ?? false;
                    const tiposDeEsteOrg = org.idTipoDocumento || [];
                    const tieneTipos = tiposDeEsteOrg.length > 0;
                    const tiposElegidosCount = getTiposSeleccionadosDeOrg(org.id).length;

                    return [
                      <MenuItem
                        key={`org-${org.id}`}
                        disableGutters
                        sx={{ px: 1, borderBottom: "1px solid", borderColor: "divider", backgroundColor: isSelected ? "primary.50" : "transparent", "&:hover": { backgroundColor: "action.hover" } }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5 }}>
                          <Checkbox checked={isSelected} onChange={(e) => handleOrganismoChange(org.id, e.target.checked)} onClick={(e) => e.stopPropagation()} size="small" />
                          <ListItemText primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">{org.nombre}</Typography>
                              {org.sigla && <Chip label={org.sigla} size="small" variant="outlined" />}
                              {isSelected && tiposElegidosCount > 0 && <Chip label={`${tiposElegidosCount} tipos`} size="small" color="secondary" />}
                            </Box>
                          } />
                          {tieneTipos && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleExpand(org.id); }} sx={{ ml: "auto", flexShrink: 0 }}>
                              <ExpandMoreIcon fontSize="small" sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
                            </IconButton>
                          )}
                        </Box>
                      </MenuItem>,
                      ...(isExpanded && isSelected && tieneTipos
                        ? tiposDeEsteOrg.map((tipoId, idx) => (
                            <MenuItem
                              key={`tipo-${org.id}-${tipoId}`}
                              disableGutters
                              sx={{ pl: 4, pr: 1, py: 0.5, borderBottom: idx === tiposDeEsteOrg.length - 1 ? "1px solid" : "none", borderColor: "divider", backgroundColor: "grey.50", "&:hover": { backgroundColor: "action.hover" } }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox size="small" checked={isTipoSelected(org.id, tipoId)} onChange={(e) => handleTipoChange(org.id, tipoId, e.target.checked)} onClick={(e) => e.stopPropagation()} />
                              <ListItemText primary={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <DocumentIcon fontSize="small" color="action" />
                                  <Typography variant="body2">{org.tipoDocumento?.[idx]}</Typography>
                                </Box>
                              } />
                            </MenuItem>
                          ))
                        : []),
                    ];
                  })}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={handleBuscar}
                disabled={loading || !fechaDesde || !fechaHasta}
                sx={{ minWidth: 140, height: 40 }}
              >
                {loading ? "Buscando..." : "Buscar normas"}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {newsletterData && (
          <Grid item>
            <Paper sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Tooltip title="Genera un PDF con texto seleccionable. En el diálogo de impresión selecciona 'Guardar como PDF'.">
                <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
                  Exportar PDF
                </Button>
              </Tooltip>
              <Tooltip title="Descarta el documento actual y permite generar uno nuevo">
                <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleLimpiar}>
                  Limpiar documento
                </Button>
              </Tooltip>
              <Box sx={{ ml: "auto" }}>
                <Alert severity="info" sx={{ py: 0 }}>
                  {newsletterData.totalNormas} normas — {newsletterData.grupos.length} organismos
                  {" · "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    Guardado automáticamente
                  </Typography>
                </Alert>
              </Box>
            </Paper>
          </Grid>
        )}

        {newsletterData && (
          <Grid item>
            <NewsletterPreview
              ref={printRef}
              data={newsletterData}
              descripcion={descripcion}
              onDescripcionChange={setDescripcion}
              destacado={destacado}
              onDestacadoChange={setDestacado}
              footer={footer}
              onFooterChange={setFooter}
              fechaDesde={fechaDesde}
              fechaHasta={fechaHasta}
              numeroInforme={numeroInforme}
              eliminadas={eliminadas}
              onEliminar={handleEliminar}
              edicionesCeldas={edicionesCeldas}
              onEditarCelda={handleEditarCelda}
            />
          </Grid>
        )}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default Newsletter;